import React, { useState, useEffect } from 'react'
import { PageHeader } from '@/components/layout'
import { apiClient } from '@/services/api'
import { clearPaymentConfigCache } from '@/config/payment'

interface ConfigItem {
  key: string
  value: string
  valueType: string
  description: string
}

export const AdminBankConfigPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [bankName, setBankName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [accountHolderName, setAccountHolderName] = useState('')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    fetchConfigs()
  }, [])

  const fetchConfigs = async () => {
    try {
      const response = await apiClient.get<ConfigItem[]>('/admin/config')
      const data = response.data || []
      const bankNameConfig = data.find((c: ConfigItem) => c.key === 'payment_bank_name')
      const accountNumberConfig = data.find((c: ConfigItem) => c.key === 'payment_account_number')
      const accountHolderConfig = data.find((c: ConfigItem) => c.key === 'payment_account_holder_name')
      setBankName(bankNameConfig?.value || '')
      setAccountNumber(accountNumberConfig?.value || '')
      setAccountHolderName(accountHolderConfig?.value || '')
    } catch (err) {
      setError('Gagal memuat konfigurasi')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      await Promise.all([
        apiClient.put('/admin/config/payment_bank_name', { value: bankName }),
        apiClient.put('/admin/config/payment_account_number', { value: accountNumber }),
        apiClient.put('/admin/config/payment_account_holder_name', { value: accountHolderName }),
      ])
      clearPaymentConfigCache()
      setSuccess('Konfigurasi bank berhasil disimpan')
    } catch (err) {
      setError('Gagal menyimpan konfigurasi')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-white/10 rounded w-64" />
          <div className="h-64 bg-gray-200 dark:bg-white/10 rounded-xl" />
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Konfigurasi Bank Pembayaran"
        subtitle="Atur informasi rekening bank untuk pembayaran manual transfer"
      />

      <div className="bg-white dark:bg-surface-card rounded-xl border border-gray-200 dark:border-white/5 p-6 max-w-xl">
        {success && (
          <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg text-sm border border-emerald-200 dark:border-emerald-500/20">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg text-sm border border-red-200 dark:border-red-500/20">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="bank-name" className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
              Nama Bank
            </label>
            <input
              id="bank-name"
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="contoh: BCA, Mandiri, BRI"
            />
          </div>

          <div>
            <label htmlFor="account-number" className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
              Nomor Rekening
            </label>
            <input
              id="account-number"
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="contoh: 1234567890"
            />
          </div>

          <div>
            <label htmlFor="account-holder" className="block text-xs font-bold text-gray-500 dark:text-text-muted uppercase tracking-wider mb-1">
              Nama Pemegang Rekening
            </label>
            <input
              id="account-holder"
              type="text"
              value={accountHolderName}
              onChange={(e) => setAccountHolderName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="contoh: PT Academy Indonesia"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !bankName || !accountNumber || !accountHolderName}
            className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-bold hover:opacity-90 disabled:opacity-50 transition-all shadow-lg shadow-primary/20"
          >
            {saving ? 'Menyimpan...' : 'Simpan Konfigurasi'}
          </button>
        </div>
      </div>
    </div>
  )
}
